// Mentions tab - paginated feed + chart + filters + right-rail summaries.
import './brand-mentions.scss';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { createPortal } from 'react-dom';
import {
    bp_mentions, bp_chart,
    bp_influencers, bp_filters,
    bp_undiscovered_sources, bp_assistant_ask,
    bp_visit_mention, bp_unvisit_mention, bp_mute_domain, bp_set_mention_verdict,
    bp_translate_mention,
    bp_list_mention_tags, bp_apply_mention_tag, bp_unapply_mention_tag,
    bp_delete_mention_tag,
    bp_list_report_queue, bp_clear_report_queue,
    bp_queue_mention, bp_unqueue_mention,
    bp_request_mention_takedown,
    bp_rescan_mentions, bp_scan_status,
    bp_mentions_since, bp_anchor_preview,
    bp_alert_latency,
    bp_mention_comments,
} from '../../../services';
import CommonPagination from '../../../Components/CommonPagination';
import ReactMarkdown from 'react-markdown';
import CommonButton from '../../../Components/CommonButton';
import { useConfirm } from '../../../Components/ConfirmModal';
import CommonDropdown from '../../../Components/CommonDropdown';
import {
    SENTIMENT_COLOR, formatNumber, timeAgo, unwrap, formatLocalDateTime,
    Skeleton, EmptyState, highlightHashtags, mdNormalize, NOVA_LOGO_SRC
} from './_shared';
import foreseeLogo from '../../../NovaWidget/assets/foresee-eagle.png';
import { AccountAvatar, PlatformChip, profileUrlFor } from './_avatarKit';
import MentionFilters from './MentionFilters';
import { LanguageBreakdownModal, useLanguageData } from './LanguageAnalysis';
import FullPageLoader from '../../../Components/FullPageLoader';
import CommonSearchBar from '../../../Components/CommonSearchBar';
import StatusBadge from '../../../Components/StatusBadge';
import { useToast } from '../../../Components/CommonToast';
import CommonTabs from '../../../Components/CommonTabs';
import TooltipText from '../../../Components/TooltipText';
import { Body, SubHeading } from '../../../Constants/Fonts';

// Custom-rendered (not native <select>) so every state - rest, hover,
// selected - is drawn by us instead of the OS's popup chrome, which ignores
// CSS for the highlighted row on Windows Chrome.
const DAYS_OPTIONS = [
    { value: 1, label: 'Last 24 hours' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
];
const SORT_OPTIONS = [
    { value: '-posted_at', label: 'Newest' },
    { value: 'posted_at', label: 'Oldest' },
    { value: '-reach_score', label: 'Highest reach' },
];

// Platforms with a working "Show Comments" fetch path.
// tiktok and youtube added 2026-09-17: the backend helpers existed all along
// but were never wired into comment_fetchers, so the button was hidden for
// platforms that could in fact answer.
const COMMENT_PLATFORMS = [
    'x', 'bluesky', 'reddit', 'facebook', 'instagram', 'linkedin',
    'tiktok', 'youtube',
];

const PLATFORM_DISPLAY = {
    x: 'X', twitter: 'Twitter', bluesky: 'Bluesky', reddit: 'Reddit',
    facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn',
    tiktok: 'TikTok', youtube: 'YouTube',
};
const fmtPlatform = p => (p && (PLATFORM_DISPLAY[p.toLowerCase()] || (p.charAt(0).toUpperCase() + p.slice(1)))) || 'Source';

// Brand24-parity: flag the days where mention volume is anomalously high
// so the user's eye lands on the spike before they scroll. Threshold is
// mean + 1.5σ over the full visible window, with a floor of 2× the mean
// so a series of all-zeros plus one (2) doesn't false-positive every day.
function spikeStats(series) {
    if (!series || series.length < 4) return { spikes: new Set(), mean: 0, stddev: 0, threshold: 0 };
    const values = series.map(d => d.mentions || 0);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    if (mean === 0) return { spikes: new Set(), mean: 0, stddev: 0, threshold: 0 };
    const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length;
    const stddev = Math.sqrt(variance);
    const threshold = Math.max(mean + 1.5 * stddev, mean * 2);
    const spikes = new Set(
        series.map((d, i) => ((d.mentions || 0) > threshold && (d.mentions || 0) > 2) ? i : null)
            .filter(i => i !== null)
    );
    return { spikes, mean, stddev, threshold };
}

const BUCKET_UNIT = { days: 'daily', weeks: 'weekly', months: 'monthly' };
const fmtSpikeNum = n => String(n >= 10 ? Math.round(n) : parseFloat(n.toFixed(1)));
const spikeLabel = s => `${fmtSpikeNum(s.ratio)}× the usual ${s.unit} mentions (avg ${fmtSpikeNum(s.mean)})`;

// Bucket labels are YYYY-MM-DD, YYYY-Www or YYYY-MM. Date.parse only reads the
// first (the others are NaN), so each is parsed explicitly; returns the UTC start.
const DAY_MS = 86400000;
export function bucketStartMs(label) {
    if (!label) return NaN;
    const wk = /^(\d{4})-W(\d{2})$/.exec(label);
    if (wk) {
        const year = +wk[1], week = +wk[2];
        // Monday of ISO week 1 (the week containing Jan 4).
        const jan4Mon = (new Date(Date.UTC(year, 0, 4)).getUTCDay() + 6) % 7;
        return Date.UTC(year, 0, 4) - jan4Mon * DAY_MS + (week - 1) * 7 * DAY_MS;
    }
    const mo = /^(\d{4})-(\d{2})$/.exec(label);
    if (mo) return Date.UTC(+mo[1], +mo[2] - 1, 1);
    const t = Date.parse(label);
    return Number.isFinite(t) ? t : NaN;
}

function fmtDMY(iso) {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return d && m && y ? `${d}-${m}-${y}` : iso;
}

function bucketRange(label) {
    const ymd = ms => new Date(ms).toISOString().slice(0, 10);
    const start = bucketStartMs(label);
    if (!Number.isFinite(start)) return { from: label, to: label, period: 'day' };
    const mo = /^(\d{4})-(\d{2})$/.exec(label);
    if (mo) return { from: ymd(start), to: ymd(Date.UTC(+mo[1], +mo[2], 0)), period: 'month' };
    if (/^\d{4}-W\d{2}$/.test(label)) return { from: ymd(start), to: ymd(start + 6 * DAY_MS), period: 'week' };
    return { from: label, to: label, period: 'day' };
}

// Format reach / view counts the way industry-standard analytics dashboards
// do: 1.2k / 3.4M / 1.1B. Keeps tooltip values legible on small charts.
function fmtCompact(n) {
    if (n == null || isNaN(n)) return '-';
    const abs = Math.abs(n);
    if (abs >= 1e9) return (n / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
    if (abs >= 1e6) return (n / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
    if (abs >= 1e3) return (n / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'k';
    return Math.round(n).toString();
}

const CHART_H = 260;
const CHART_M = { top: 16, right: 16, bottom: 34, left: 44 };
const TRANSPARENT_PX = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function niceTicks(max) {
    const rough = Math.max(1, max) / 4;
    const pow = 10 ** Math.floor(Math.log10(rough));
    const n = rough / pow;
    const step = Math.max(1, (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow);
    return [0, 1, 2, 3, 4].map(i => i * step);
}

function useElementWidth(ref) {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        setWidth(el.clientWidth);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(entries => setWidth(Math.round(entries[0].contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return width;
}

const CHART_SERIES = {
    mentions_reach: [
        { key: 'mentions', label: 'Mentions', kind: 'line' },
    ],
    sentiment: [
        { key: 'positive', label: 'Positive', kind: 'bar' },
        { key: 'neutral', label: 'Neutral', kind: 'bar' },
        { key: 'negative', label: 'Negative', kind: 'bar' },
    ],
};

function ChartTip({ d, mode, spike }) {
    return (
        <>
            <Body as="span" variant={2} weight="semibold" className="bm_chart_tip_date">{d.bucket}</Body>
            {CHART_SERIES[mode].map(s => (
                <span key={s.key} className="bm_chart_tip_row">
                    <span className={`bm_chart_tip_dot is-${s.key}${s.kind === 'bar' ? ' is-bar' : ''}`} aria-hidden="true" />
                    <Body as="span" variant={2} className="bm_chart_tip_label">{s.label}</Body>
                    <Body as="span" variant={2} weight="semibold" className="bm_chart_tip_val">{fmtCompact(d[s.key] || 0)}</Body>
                </span>
            ))}
            {spike && (
                <Body as="span" variant={2} className="bm_chart_tip_note">
                    Spike: {spikeLabel(spike)}. Click <strong>!</strong> to see the posts.
                </Body>
            )}
        </>
    );
}

export function MentionsReachChart({ series, mode, bucket, onSpikeClick, onPointClick, showSpikes = true }) {
    const wrapRef = useRef(null);
    const width = useElementWidth(wrapRef);
    const [hoverIdx, setHoverIdx] = useState(null);
    const gid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
    const isSentiment = mode === 'sentiment';
    const modeKey = isSentiment ? 'sentiment' : 'mentions_reach';
    const barKeys = CHART_SERIES[modeKey].filter(s => s.kind === 'bar').map(s => s.key);
    const lineKeys = CHART_SERIES[modeKey].filter(s => s.kind === 'line').map(s => s.key).reverse();
    const hasBars = barKeys.length > 0;
    const data = series || [];
    const n = data.length;

    useEffect(() => { setHoverIdx(null); }, [series, mode]);

    const W = width;
    const innerW = Math.max(0, W - CHART_M.left - CHART_M.right);
    const innerH = CHART_H - CHART_M.top - CHART_M.bottom;
    const rawMax = n === 0 ? 1 : Math.max(1, ...data.flatMap(d => [...barKeys, ...lineKeys].map(k => d[k] || 0)));
    const ticks = niceTicks(rawMax);
    const yMax = ticks[ticks.length - 1];
    const y = v => CHART_M.top + innerH - (v / yMax) * innerH;
    const band = n ? innerW / n : innerW;
    const x = i => (hasBars
        ? CHART_M.left + (i + 0.5) * band
        : CHART_M.left + (n <= 1 ? innerW / 2 : (i * innerW) / (n - 1)));
    const barGap = Math.min(2, band * 0.06);
    const barW = hasBars
        ? Math.max(1, Math.min(18, (band * 0.6 - barGap * (barKeys.length - 1)) / barKeys.length))
        : 0;
    const groupW = hasBars ? barW * barKeys.length + barGap * (barKeys.length - 1) : 0;
    const barX = (i, j) => x(i) - groupW / 2 + j * (barW + barGap);

    const { spikes, mean } = (isSentiment || !showSpikes) ? { spikes: new Set(), mean: 0 } : spikeStats(data);
    const spikeOf = d => ({ ratio: (d.mentions || 0) / mean, mean, unit: BUCKET_UNIT[bucket] || BUCKET_UNIT.days });

    const labelStep = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(innerW / 90))));
    const labelled = data.map((_, i) => i).filter(i => i % labelStep === 0);

    const lineFor = key => d3.line().x((d, i) => x(i)).y(d => y(d[key] || 0)).curve(d3.curveMonotoneX)(data);
    const areaFor = key => d3.area().x((d, i) => x(i)).y0(y(0)).y1(d => y(d[key] || 0)).curve(d3.curveMonotoneX)(data);

    const targets = [];
    data.forEach((d, i) => {
        if (hasBars) targets.push({ i, key: 'col', column: true });
        else lineKeys.forEach(k => targets.push({ i, key: k, cy: y(d[k] || 0) }));
    });

    return (
        <div className="bm_chart_wrap" ref={wrapRef}>
            {n === 0 && <Body className="bm_chart_empty">No mentions in this date range.</Body>}
            {n > 0 && W > 0 && (
                <>
                    <svg className="bm_chart_svg" width={W} height={CHART_H} role="img"
                        aria-label={isSentiment ? 'Sentiment over time' : 'Mentions over time'}>
                        <defs>
                            {!hasBars && lineKeys.map(k => (
                                <linearGradient key={k} id={`${gid}-${k}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" className={`bm_chart_stop is-${k}`} stopOpacity="0.28" />
                                    <stop offset="100%" className={`bm_chart_stop is-${k}`} stopOpacity="0.02" />
                                </linearGradient>
                            ))}
                        </defs>

                        <g className="bm_chart_axis">
                            {ticks.map(t => (
                                <g key={'y' + t}>
                                    <line className="bm_chart_grid" x1={CHART_M.left} x2={W - CHART_M.right} y1={y(t)} y2={y(t)} />
                                    <text x={CHART_M.left - 10} y={y(t)} dy="0.32em" textAnchor="end">{fmtCompact(t)}</text>
                                </g>
                            ))}
                            {labelled.map(i => (
                                <g key={'x' + i}>
                                    {!hasBars && (
                                        <line className="bm_chart_grid is-vertical" x1={x(i)} x2={x(i)} y1={CHART_M.top} y2={y(0)} />
                                    )}
                                    <text x={x(i)} y={CHART_H - 10} textAnchor="middle">{data[i].bucket}</text>
                                </g>
                            ))}
                        </g>

                        {hasBars && (
                            <>
                                {data.map((d, i) => (
                                    <g key={'bars-' + d.bucket} className={`bm_chart_bar ${hoverIdx != null && hoverIdx !== i ? 'is-dim' : ''}`}>
                                        {barKeys.map((k, j) => d[k] > 0 && (
                                            <rect key={k} className={`is-${k}`} x={barX(i, j)} y={y(d[k])}
                                                width={barW} height={y(0) - y(d[k])} rx={Math.min(3, barW / 2)} />
                                        ))}
                                    </g>
                                ))}
                                <line className="bm_chart_baseline" x1={CHART_M.left} x2={W - CHART_M.right} y1={y(0)} y2={y(0)} />
                            </>
                        )}

                        {n > 1 && (
                            <>
                                {!hasBars && lineKeys.map(k => (
                                    <path key={'area-' + k} d={areaFor(k)} fill={`url(#${gid}-${k})`} />
                                ))}
                                {lineKeys.map(k => (
                                    <path key={'line-' + k} className={`bm_chart_line is-${k}`} d={lineFor(k)} />
                                ))}
                            </>
                        )}
                        {n === 1 && lineKeys.map(k => (
                            <line key={k} className={`bm_chart_level is-${k}`}
                                x1={CHART_M.left} x2={W - CHART_M.right}
                                y1={y(data[0][k] || 0)} y2={y(data[0][k] || 0)} />
                        ))}
                        {hoverIdx != null && !hasBars && (
                            <line className="bm_chart_guide" x1={x(hoverIdx)} x2={x(hoverIdx)} y1={CHART_M.top} y2={y(0)} />
                        )}
                        {data.map((d, i) => lineKeys.map(k => (
                            <g key={k + i}>
                                {hoverIdx === i && <circle className={`bm_chart_halo is-${k}`} cx={x(i)} cy={y(d[k] || 0)} r="10" />}
                                <circle className={`bm_chart_dot is-${k}`} cx={x(i)} cy={y(d[k] || 0)} r={hoverIdx === i ? 5 : 4} />
                            </g>
                        )))}
                        {[...spikes].map(i => {
                            const d = data[i];
                            const cx = x(i), cy = y(d.mentions || 0);
                            return (
                                <g key={'spike-' + i} className="bm_spike_marker"
                                    onClick={() => onSpikeClick && onSpikeClick({
                                        index: i, bucket: d.bucket, range: bucketRange(d.bucket),
                                        mentions: d.mentions || 0, reach: d.reach || 0,
                                        ...spikeOf(d),
                                        cxPct: (cx / W) * 100, cyPx: cy,
                                    })}>
                                    <circle className="bm_spike_hit" cx={cx} cy={cy - 28} r="16" />
                                    <line className="bm_spike_stem" x1={cx} y1={cy - 6} x2={cx} y2={cy - 20} />
                                    <circle className="bm_spike_dot" cx={cx} cy={cy - 28} r="9" />
                                    <text className="bm_spike_bang" x={cx} y={cy - 24} textAnchor="middle">!</text>
                                </g>
                            );
                        })}
                    </svg>

                    <div className="bm_chart_hits">
                        {targets.map(t => (
                            <div key={t.key + t.i} className={`bm_chart_hit${t.column ? ' is-column' : ''}`}
                                style={t.column
                                    ? { left: x(t.i) - band / 2, top: CHART_M.top, width: band, height: innerH }
                                    : { left: x(t.i), top: t.cy, cursor: onPointClick ? 'pointer' : undefined }}
                                onMouseEnter={() => setHoverIdx(t.i)}
                                onMouseLeave={() => setHoverIdx(null)}
                                onClick={() => onPointClick && data[t.i]?.bucket && onPointClick(bucketRange(data[t.i].bucket))}>
                                <TooltipText
                                    src={TRANSPARENT_PX}
                                    alt=""
                                    className={`bm_chart_tip ${x(t.i) > W * 0.62 ? 'is-flip' : ''} ${!t.column && t.cy < CHART_M.top + 90 ? 'is-below' : ''}`}
                                    tooltipText={<ChartTip d={data[t.i]} mode={modeKey}
                                        spike={spikes.has(t.i) ? spikeOf(data[t.i]) : null} />}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// CEPI RFP row 6 - misinfo SLA: every misinformation-tagged mention must be
// detected within 24h of being posted. Narrative tags here mirror the
// scanner's misinfo taxonomy (see scanner/utils/brand_mention_scan.py's
// NARRATIVE_TAGS); kept as a Set so the membership check stays O(1).
const MISINFO_NARRATIVES = new Set([
    'misinformation', 'disinformation', 'vaccine_misinformation',
    'vaccine_safety_misinfo', 'safety_concern', 'conspiracy',
    'coordinated_campaign',
]);
const MISINFO_SLA_HOURS = 24;

function misinfoSlaStatus(m) {
    if (!m.posted_at) return null;
    if (!MISINFO_NARRATIVES.has(m.narrative_tag)) return null;
    const ageHours = (Date.now() - new Date(m.posted_at).getTime()) / 36e5;
    return {
        ageHours,
        met: ageHours <= MISINFO_SLA_HOURS,
        deadline: MISINFO_SLA_HOURS,
    };
}

// CEPI RFP #28 - Alert-latency SLA pill.
// Renders p95 detection-to-alert time over the last 30 days plus the % of
// alerts that landed inside the 24h SLA. Tooltip shows p50 / p90 / p99 +
// per-platform breakdown so a customer can see where latency concentrates.
function _fmtLatency(seconds) {
    if (seconds == null) return '-';
    const s = Number(seconds);
    if (!isFinite(s) || s < 0) return '-';
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
    return `${(s / 86400).toFixed(1)}d`;
}

function AlertLatencyPill({ data }) {
    if (!data || !data.measured || data.measured <= 0) return null;
    const p = data.percentiles_seconds || {};
    const slaPct = data.sla_compliance_pct;
    const ok = slaPct == null ? true : slaPct >= 95;
    const palette = ok
        ? { bg: 'rgba(16,185,129,0.12)', fg: '#047857', border: 'rgba(16,185,129,0.45)', dot: '#10b981' }
        : { bg: 'rgba(245,158,11,0.15)', fg: '#b45309', border: 'rgba(245,158,11,0.55)', dot: '#f59e0b' };
    const byPlat = (data.by_platform || []).slice(0, 5)
        .map(b => `  ${b.platform}: p50 ${_fmtLatency(b.p50)} · p95 ${_fmtLatency(b.p95)} (${b.count})`)
        .join('\n');
    const title = (
        `Alert latency over last ${data.window_days || 30} days: time between ` +
        `post going live and our scanner persisting it.\n` +
        `Measured on ${data.measured} of ${data.total} mentions.\n` +
        `p50 ${_fmtLatency(p.p50)} · p90 ${_fmtLatency(p.p90)} · ` +
        `p95 ${_fmtLatency(p.p95)} · p99 ${_fmtLatency(p.p99)} · ` +
        `max ${_fmtLatency(p.max)}.\n` +
        `SLA: ${slaPct == null ? '-' : slaPct + '%'} inside 24h.` +
        (byPlat ? `\n\nBy platform:\n${byPlat}` : '')
    );
    return (
        <span title={title} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '2px 9px', borderRadius: 999,
            background: palette.bg, color: palette.fg,
            border: `1px solid ${palette.border}`,
            fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
            cursor: 'help',
        }}>
            <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: palette.dot,
            }} />
            Alert p95 {_fmtLatency(p.p95)}
            {slaPct != null && (
                <span style={{ opacity: 0.85 }}>· SLA {slaPct}%</span>
            )}
        </span>
    );
}

function Pagination({ page, pages, total, pageSize, onPage, onPageSize }) {
    if (!total) return null;
    const startIdx = (page - 1) * pageSize + 1;
    const endIdx = Math.min(page * pageSize, total);
    return (
        <div className="bm_pagination">
            <div className="bm_pagination_info">
                <Body as="span" variant={2} className="bm_pagination_label">
                    Showing <strong>{startIdx.toLocaleString()}-{endIdx.toLocaleString()}</strong>{' '}
                    of <strong>{total.toLocaleString()}</strong> mentions
                </Body>
                {onPageSize && (
                    <span className="bm_pagination_size">
                        <Body as="span" variant={2} className="bm_pagination_label">Per page</Body>
                        <CommonDropdown
                            ariaLabel="Per page"
                            value={pageSize}
                            onChange={onPageSize}
                            options={[10, 25, 50, 100].map(n => ({ value: n, label: n }))}
                            minWidth={72}
                        />
                    </span>
                )}
            </div>
            {pages > 1 && (
                <CommonPagination
                    pageCount={pages}
                    forcePage={page - 1}
                    handlePageClick={({ selected }) => onPage(selected + 1)}
                />
            )}
        </div>
    );
}

function hostFromUrl(url) {
    try { return new URL(url).hostname.replace(/^www\./, ''); }
    catch (_) { return ''; }
}

// Human-readable label for the org's scheduled-scan cadence. Mirrors the
// labels in SettingsTab's tier dropdown so the rate-limit banner agrees
// with whatever the admin picked there.
function cadenceLabel(minutes) {
    if (minutes == null) return 'on schedule';
    if (minutes === 0) return 'are turned off';
    if (minutes < 60) return `every ${minutes} min`;
    if (minutes === 60) return 'every hour';
    if (minutes % 60 === 0) return `every ${minutes / 60} hours`;
    return `every ${minutes} min`;
}

// The backend's rate_limited message hardcodes "every 2h" regardless of
// the org's actual cadence. Preserve the "Try again in ~N min" portion
// (server-computed cooldown) but rewrite the tail using the cadence the
// admin selected in Settings.
function formatRateLimitedMessage(backendMessage, cadenceMinutes) {
    const cad = cadenceLabel(cadenceMinutes);
    const tail = cadenceMinutes === 0
        ? 'Scheduled scans are turned off. Only manual scans run.'
        : `Scheduled scans still run ${cad}.`;
    if (!backendMessage) return `Manual scan rate-limited. ${tail}`;
    const tryAgain = backendMessage.match(/Try again[^.]*\./i);
    return tryAgain
        ? `Manual scan rate-limited. ${tryAgain[0]} ${tail}`
        : `Manual scan rate-limited. ${tail}`;
}

function SpikePanel({ spike, onClose, onDrillIn, onShowAll }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!spike) return;
        let alive = true;
        setLoading(true);
        bp_mentions({
            from_date: spike.range.from,
            to_date: spike.range.to,
            sort: '-reach_score',
            page_size: 5,
            days: 90,
        }).then(res => {
            if (!alive) return;
            const body = unwrap(res) || {};
            setItems(body.results || []);
        }).catch(() => alive && setItems([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [spike]);

    if (!spike) return null;

    const { from, to, period } = spike.range;
    const isDay = period === 'day';

    return (
        <div className="bm_spike_panel"
            role="dialog" aria-label={`Spike details for ${spike.bucket}`}>
            <div className="bm_spike_panel_head">
                <div>
                    <div className="bm_spike_panel_label">{isDay ? `Spike on ${from}` : `Spike: ${from} to ${to}`}</div>
                    <div className="bm_spike_panel_stats">
                        <span><strong>{spike.mentions}</strong> mentions</span>
                        <span><strong>{Math.round(spike.reach)}</strong> reach</span>
                        <span className="bm_spike_sigma">{spikeLabel(spike)}</span>
                    </div>
                </div>
                <button onClick={onClose} aria-label="Close spike detail"
                    className="bm_spike_panel_close">×</button>
            </div>

            <div className="bm_spike_panel_body">
                {loading && <div style={{ color: '#94a3b8', fontSize: 12 }}>Loading top posts for this {period}…</div>}
                {!loading && items.length === 0 && (
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>
                        No posts found for this {period}.
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <ul className="bm_spike_panel_items">
                        {items.map(m => (
                            <li key={m.id}>
                                <span className="bm_spike_item_meta">
                                    <strong>{m.handle || '(no handle)'}</strong> · {m.platform}
                                    {m.reach_score ? <em> · reach {Math.round(m.reach_score)}</em> : null}
                                </span>
                                <span className="bm_spike_item_text">
                                    {(m.text || '').slice(0, 200)}{(m.text || '').length > 200 ? '…' : ''}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="bm_spike_panel_foot">
                <CommonButton
                    text={isDay ? `Filter feed to ${from}` : `Filter feed to this ${period}`}
                    variant="primary"
                    className="bm_scan_btn"
                    onClick={() => { onDrillIn(spike); onClose(); }}
                />
                <CommonButton
                    text="Show all"
                    variant="white"
                    className="bm_scan_btn"
                    onClick={() => { onShowAll(); onClose(); }}
                />
            </div>
        </div>
    );
}

export function TakedownModal({ mention, onClose, onSubmitted }) {
    const [reason, setReason] = useState('');
    const [targetKind, setTargetKind] = useState('post');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setBusy(true); setError('');
        try {
            const res = await bp_request_mention_takedown(mention.id, { reason, target_kind: targetKind });
            const data = res?.statusCode?.response_data?.data ?? res?.statusCode ?? {};
            onSubmitted && onSubmitted(mention.id, {
                id: data.takedown_request_id,
                status: data.status_value || 'requested',
                target_kind: data.target_kind,
                sla_initiate_target: data.sla_initiate_target,
                sla_resolve_target: data.sla_resolve_target,
                created_date: new Date().toISOString(),
                already_existed: data.created === false,
            });
            onClose();
        } catch (e) {
            setError('Could not submit takedown. Try again.');
        } finally { setBusy(false); }
    };

    return createPortal((
        <div className="bm_modal_overlay" onClick={onClose}>
            <div className="bm_modal" onClick={e => e.stopPropagation()}
                role="dialog" aria-label="Request takedown">
                <div className="bm_modal_head">
                    <h4>Request takedown</h4>
                    <button onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="bm_modal_body">
                    <p style={{ fontSize: 12, color: '#475569', margin: '0 0 12px' }}>
                        Submit a takedown request through NMT’s abuse-channel
                        dispatcher (X / Reddit / Cloudflare / host / registrar).
                        SLA timers start the moment you click Submit:
                        <strong> initiate ≤ 24h, resolve ≤ 5 days.</strong>
                    </p>
                    <div className="bm_modal_target">
                        <div className="bm_modal_target_post"><strong>{mention.handle}</strong> · {mention.platform}</div>
                        <div className="bm_modal_target_text">{(mention.text || '').slice(0, 280)}{(mention.text || '').length > 280 ? '…' : ''}</div>
                        <a href={mention.url} target="_blank" rel="noopener noreferrer"
                            className="bm_modal_target_url">{mention.url}</a>
                    </div>

                    <div className="bm_modal_kind">
                        <label className={targetKind === 'post' ? 'on' : ''}>
                            <input type="radio" name="kind" value="post"
                                checked={targetKind === 'post'}
                                onChange={() => setTargetKind('post')} />
                            <div>
                                <strong>This post only</strong>
                                <small>Removes the specific URL via the platform’s abuse channel. Right choice for X / Reddit / Bluesky posts.</small>
                            </div>
                        </label>
                        <label className={targetKind === 'domain' ? 'on' : ''}>
                            <input type="radio" name="kind" value="domain"
                                checked={targetKind === 'domain'}
                                onChange={() => setTargetKind('domain')} />
                            <div>
                                <strong>Whole domain</strong>
                                <small>Reports the entire host. Right choice for phishing / typosquat sites.</small>
                            </div>
                        </label>
                    </div>

                    <label className="bm_modal_reason">
                        <span>Reason (optional)</span>
                        <textarea rows={3} value={reason}
                            placeholder="Defamatory / impersonation / phishing / etc."
                            onChange={e => setReason(e.target.value)} />
                    </label>

                    {error && <div className="bm_modal_error">{error}</div>}
                </div>
                <div className="bm_modal_foot">
                    <CommonButton
                        text="Cancel"
                        variant="white"
                        className="bm_scan_btn"
                        onClick={onClose} disable={busy}
                    />
                    <CommonButton
                        text={busy ? 'Submitting…' : 'Submit takedown request'}
                        variant="primary"
                        className="bm_scan_btn"
                        onClick={submit} disable={busy}
                    />
                </div>
            </div>
        </div>
    ), document.body);
}

// Read comments from a few common API response shapes so a backend rename
// doesn't break the UI.
function extractComments(body) {
    const data = body?.response_data?.data ?? body?.data ?? body ?? {};
    const list = data.comments || data.results || data.items || [];
    const total = data.total ?? data.total_count ?? data.count ?? list.length;
    return { list: Array.isArray(list) ? list : [], total };
}

function CommentsModal({ mention, onClose, onLoaded }) {
    const PAGE_SIZE = 10;
    const [page, setPage] = useState(0);              // 0-indexed for ReactPaginate
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [allComments, setAllComments] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true); setError('');
            try {
                const res = await bp_mention_comments(mention.id, { refresh: 1 });
                if (cancelled) return;
                const body = res?.statusCode ?? res?.data ?? res;
                const { list } = extractComments(body);
                setAllComments(list);
                setPage(0);
                // Tell the card what we found. This request is what populates
                // the comments in the first place, so its result is newer than
                // anything the feed was rendered with.
                onLoaded && onLoaded(list.length);
            } catch (e) {
                if (!cancelled) setError('Could not load comments. Try again.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [mention.id]);

    const total = allComments.length;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const comments = allComments.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const bodyRef = useRef(null);
    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page]);

    return createPortal((
        <div className="bm_modal_overlay" onClick={onClose}>
            <div className="bm_modal bm_modal--comments" onClick={e => e.stopPropagation()}
                role="dialog" aria-label="Comments">
                <div className="bm_modal_head">
                    <h4>Comments {total > 0 && <span style={{ color: '#64748b', fontWeight: 500 }}>· {total}</span>}</h4>
                    <button onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="bm_modal_body" ref={bodyRef}>
                    {/* <div className="bm_modal_target">
                        <div className="bm_modal_target_post">
                            <strong>{mention.handle}</strong> · {mention.platform}
                        </div>
                        <div className="bm_modal_target_text">
                            {(mention.text || '').slice(0, 280)}{(mention.text || '').length > 280 ? '…' : ''}
                        </div>
                    </div> */}

                    {loading && <div className="bm_comments_state">Loading comments…</div>}
                    {error && !loading && <div className="bm_modal_error">{error}</div>}
                    {!loading && !error && comments.length === 0 && (
                        <div className="bm_comments_state">No comments yet.</div>
                    )}

                    {!loading && !error && comments.length > 0 && (
                        <ul className="bm_comments_list">
                            {comments.map((c, idx) => {
                                const profile = c.profile || c.author || c.user || {};
                                const displayName = profile.name || profile.display_name
                                    || profile.handle || profile.username || c.author_name || 'Unknown';
                                const handle = profile.handle || profile.username || c.author_handle || '';
                                const avatar = profile.avatar_url || profile.avatar || profile.image
                                    || c.author_avatar;
                                const nameMasked = c.privacy?.name_masked === true;
                                const profileUrl = nameMasked ? null
                                    : (profile.profile_url || profile.url || profile.link
                                        || (handle ? profileUrlFor(handle, mention.platform) : null));

                                const text = c.text || c.body || c.content || c.comment || '';
                                const when = c.created_at || c.posted_at || c.timestamp;
                                return (
                                    <li key={c.id || idx} className="bm_comment_row">
                                        <div className="bm_comment_avatar">
                                            {profileUrl ? (
                                                <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                                                    title={`Open ${displayName} in a new tab`}>
                                                    <AccountAvatar handle={handle || displayName}
                                                        platform={mention.platform}
                                                        handleToken={c.handle_token}
                                                        avatarUrl={avatar} size={36} />
                                                </a>
                                            ) : (
                                                <AccountAvatar handle={handle || displayName}
                                                    platform={mention.platform}
                                                    handleToken={c.handle_token}
                                                    avatarUrl={avatar} size={36} />
                                            )}
                                        </div>
                                        <div className="bm_comment_body">
                                            <div className="bm_comment_meta">
                                                {/* {profileUrl ? (
                                                    <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                                                        className="bm_comment_name">{displayName}</a>
                                                ) : (
                                                    <span className="bm_comment_name">{displayName}</span>
                                                )} */}
                                                {handle && <span className="bm_comment_handle">@{String(handle).replace(/^@/, '')}</span>}
                                                {when && <span className="bm_comment_time">· {timeAgo(when)}</span>}
                                            </div>
                                            <div className="bm_comment_text">{text}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {!loading && !error && pageCount > 1 && (
                        <div className="bm_comments_pagination">
                            <CommonPagination
                                handlePageClick={({ selected }) => setPage(selected)}
                                forcePage={page}
                                pageCount={pageCount}
                            />
                        </div>
                    )}
                </div>
                <div className="bm_modal_foot">
                    <CommonButton
                        text="Close"
                        variant="white"
                        className="bm_scan_btn"
                        onClick={onClose}
                    />
                </div>
            </div>
        </div>
    ), document.body);
}

// Brand24-parity tag palette - used when a tag has no explicit color.
const TAG_PALETTE = ['#3b82f6', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];
function tagColorFor(t, fallback) {
    return t.color || TAG_PALETTE[(t.id || fallback || 0) % TAG_PALETTE.length];
}

// Inline-style helper for the MentionCard ⋯ More popover items.
const menuItemStyle = {
    textAlign: 'left',
    padding: '6px 10px',
    background: 'transparent',
    border: 'none',
    borderRadius: 4,
    color: '#1f2937',
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
};

function MentionCard({ m, onVisitChange, onMute, onTagsChange, onQueueChange, onTakedown, onVerdictChange, onOrgTagsRefresh, orgTags = [] }) {
    const { confirm, confirmModal } = useConfirm();
    const sentiment = (m.sentiment || 'neutral').toLowerCase();
    const sentColor = SENTIMENT_COLOR[sentiment] || SENTIMENT_COLOR.neutral;
    const engagement = Number(m.engagement) || 0;
    const sla = misinfoSlaStatus(m);
    const host = hostFromUrl(m.url);
    const [busy, setBusy] = useState(false);
    const [tagPickerOpen, setTagPickerOpen] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [takedownModalOpen, setTakedownModalOpen] = useState(false);
    const [commentsModalOpen, setCommentsModalOpen] = useState(false);
    const [translation, setTranslation] = useState(m.translated_text || '');
    const [showTranslated, setShowTranslated] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [translateError, setTranslateError] = useState('');
    const lang = (m.language || '').toLowerCase();
    const canTranslate = !!(m.text && lang && lang !== 'en');

    // 2026-05-27 UX cleanup - collapse long bodies + overflow menu.
    const [bodyExpanded, setBodyExpanded] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const BODY_PREVIEW_LEN = 180;
    const appliedTagIds = new Set((m.tags || []).map(t => t.id));
    const takedown = m.takedown;
    const takedownActive = takedown && takedown.status !== 'resolved' && takedown.status !== 'failed';

    const toggleVisited = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (m.is_visited) await bp_unvisit_mention(m.id);
            else await bp_visit_mention(m.id);
            onVisitChange && onVisitChange(m.id, !m.is_visited);
        } finally { setBusy(false); }
    };

    const setVerdict = async (verdict) => {
        if (busy) return;
        setBusy(true);
        try {
            await bp_set_mention_verdict(m.id, verdict);
            onVerdictChange && onVerdictChange(m.id, verdict);
        } catch (e) {
            console.warn('verdict update failed', e);
        } finally { setBusy(false); }
    };

    const muteHost = async () => {
        if (busy || !host) return;
        const ok = await confirm({
            title: 'Mute domain',
            message: `Mute "${host}" for this org? It will be hidden from every Brand Protection feed until you unmute it in Settings.`,
            confirmLabel: 'Mute',
            danger: true,
            themeColor: false,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await bp_mute_domain({ url: m.url, reason: 'muted from Mentions card' });
            onMute && onMute(host);
        } finally { setBusy(false); }
    };

    const toggleTranslate = async () => {
        if (translation) { setShowTranslated(s => !s); return; }
        if (translating) return;
        setTranslating(true);
        setTranslateError('');
        try {
            const res = await bp_translate_mention(m.id);
            const d = unwrap(res) || {};
            if (d.translated_text) {
                const norm = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
                if (norm(d.translated_text) === norm(m.text)) {
                    setTranslateError(`Already in English (the source platform mislabelled it as ${(m.language || 'unknown').toUpperCase()}).`);
                } else {
                    setTranslation(d.translated_text);
                    setShowTranslated(true);
                }
            } else {
                setTranslateError(d.error === 'translation_unavailable'
                    ? 'FORESEE AI is currently unavailable.'
                    : 'Translation failed.');
            }
        } catch (e) {
            setTranslateError('Translation failed.');
        } finally {
            setTranslating(false);
        }
    };

    const toggleTag = async (tag) => {
        if (busy) return;
        setBusy(true);
        try {
            if (appliedTagIds.has(tag.id)) {
                await bp_unapply_mention_tag(m.id, { tag_id: tag.id });
                onTagsChange && onTagsChange(m.id, (m.tags || []).filter(t => t.id !== tag.id));
            } else {
                await bp_apply_mention_tag(m.id, { tag_id: tag.id });
                onTagsChange && onTagsChange(m.id, [...(m.tags || []), tag]);
            }
        } finally { setBusy(false); }
    };

    const createAndApply = async () => {
        const name = newTagName.trim();
        if (!name || busy) return;
        setBusy(true);
        try {
            const res = await bp_apply_mention_tag(m.id, { name });
            // Backend may return the new tag at data.tag, or directly at
            // data, depending on whether this is a create-and-apply vs an
            // apply-existing. Accept either shape so the local state
            // update doesn't silently no-op when only one shape matches.
            const data = unwrap(res) || {};
            const tag = (data.tag && data.tag.id) ? data.tag
                : (data.id ? data : null);
            if (tag) onTagsChange && onTagsChange(m.id, [...(m.tags || []), tag]);
            // Unconditional safety net: even if we couldn't extract the
            // tag object from the response, the toolbar's org-tag list
            // must show the newly-created tag. Re-fetching the canonical
            // list from the backend guarantees that.
            onOrgTagsRefresh && onOrgTagsRefresh();
            setNewTagName('');
        } finally { setBusy(false); }
    };

    const toggleQueue = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (m.is_in_report_queue) await bp_unqueue_mention(m.id);
            else await bp_queue_mention(m.id, {});
            onQueueChange && onQueueChange(m.id, !m.is_in_report_queue);
        } finally { setBusy(false); }
    };

    const profileUrl = m.privacy?.name_masked
        ? null : profileUrlFor(m.handle, m.platform);

    // Suppress the risk styling when the customer has explicitly classified
    // this mention as a false positive or chosen to ignore it - but keep
    // the underlying is_risk_tagged_source flag intact so they can undo.
    const showAsRisk = m.is_risk_tagged_source
        && m.customer_verdict !== 'false_positive'
        && m.customer_verdict !== 'ignored';
    return (
        <div className={`bm_card cmn_elevated_card sentiment-${sentiment} ${m.is_visited ? 'is-visited' : ''} ${showAsRisk ? 'is-risk' : ''}`}>
            {confirmModal}
            <div className="bm_card_avatar_col">
                {profileUrl ? (
                    <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                        className="bm_card_avatar_link"
                        title={`Open ${m.handle} on ${fmtPlatform(m.platform)}`}>
                        <AccountAvatar handle={m.handle} platform={m.platform}
                            handleToken={m.handle_token} size={44} />
                    </a>
                ) : (
                    <AccountAvatar handle={m.handle} platform={m.platform}
                        handleToken={m.handle_token} size={44} />

                )}
            </div>
            <div className="bm_card_body">
                <div className="bm_card_header_row">
                    <div className="bm_card_identity">
                        {profileUrl ? (
                            <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                                className="bm_handle bm_handle--link"
                                title={`Open ${m.handle} on ${fmtPlatform(m.platform)}`}>
                                {m.handle || '(no handle)'}
                                <span className="bm_handle_ext" aria-hidden="true">↗</span>
                            </a>
                        ) : (
                            <span className="bm_handle">{m.handle || '(no handle)'}</span>
                        )}
                        <PlatformChip platform={m.platform} />
                    </div>
                    {/* Posted vs scanned are different facts and both matter:
                        the gap is the scan cadence, and for a source that
                        publishes no timestamp posted_at falls back to the scan
                        time, so a post showing the same value for both is
                        undated at source, not necessarily brand new. */}
                    <span className="bm_dates">
                        {m.posted_at && (
                            <span className="bm_date" title={`Posted ${formatLocalDateTime(m.posted_at)}`}>
                                {timeAgo(m.posted_at)}
                            </span>
                        )}
                        {m.scanned_at && (
                            <span className="bm_date bm_date--scanned"
                                title={`Scanned ${formatLocalDateTime(m.scanned_at)}`}>
                                Scanned {timeAgo(m.scanned_at)}
                            </span>
                        )}
                    </span>
                </div>
                <div className="bm_card_meta_row">
                    {/* influence/reach dropped: only X and Reddit expose follower counts, so other platforms always showed a fake 1/10; engagement (likes+shares+comments) is reported by all */}
                    <span className="bm_engagement"
                        title={`${Number(engagement).toLocaleString()} interactions (likes, shares and comments) as reported by ${fmtPlatform(m.platform)}`}>
                        <span>Engagement</span>
                        <span className="bm_engagement_score">{formatNumber(engagement)}</span>
                    </span>
                    {/* The three counts behind `engagement`, which is their sum.
                        A 0 means the source reported none, not that none
                        exist: Instagram and YouTube publish no share count,
                        Reddit and Bluesky none either. */}
                    <span className="bm_counts" title="Likes · comments · shares, as reported by the source">
                        <span className="bm_count" title={`${Number(m.like_count || 0).toLocaleString()} likes`}>
                            <span className="bm_count_icon" aria-hidden="true">♥</span>
                            {formatNumber(Number(m.like_count) || 0)}
                        </span>
                        <span className="bm_count" title={`${Number(m.reply_count || 0).toLocaleString()} comments`}>
                            <span className="bm_count_icon" aria-hidden="true">💬</span>
                            {formatNumber(Number(m.reply_count) || 0)}
                        </span>
                        <span className="bm_count" title={`${Number(m.retweet_count || 0).toLocaleString()} shares / reposts`}>
                            <span className="bm_count_icon" aria-hidden="true">↻</span>
                            {formatNumber(Number(m.retweet_count) || 0)}
                        </span>
                    </span>
                    <span className="bm_sentiment_chip"
                        style={{
                            background: sentColor + '18',
                            color: sentColor,
                            border: `1px solid ${sentColor}40`
                        }}
                        title={`Sentiment: ${sentiment}`}>
                        {sentiment}
                    </span>
                    {m.language && (
                        <span className="bm_lang_chip"
                            title={`Detected language: ${m.language}`}>
                            {m.language}
                        </span>
                    )}

                    {takedown && (
                        <span className={`bm_takedown_chip status-${takedown.status}`}
                            title={`Takedown ${takedown.status} · ${takedown.target_kind === 'post' ? 'post-level' : 'host-level'}`}>
                            Takedown · {takedown.status}
                        </span>
                    )}
                </div>

                {/* Risk reason + customer verdict controls.
                    Shows when the row has either an active risk flag OR
                    the customer has already acted on it (so they can
                    re-review their own previous false_positive/ignored).
                    On 'false_positive', the classifier suppresses the same
                    flag on subsequent scans for the same publisher+slug. */}


                <div className="bm_card_text">
                    {(() => {
                        const body = (showTranslated && translation) ? translation : (m.text || '');
                        return highlightHashtags(
                            bodyExpanded ? body : body.slice(0, BODY_PREVIEW_LEN)
                        );
                    })()}
                    {!bodyExpanded && ((showTranslated && translation) ? translation : (m.text || '')).length > BODY_PREVIEW_LEN && (
                        <>
                            {'… '}
                            <button type="button"
                                onClick={() => setBodyExpanded(true)}
                                style={{
                                    background: 'none', border: 'none', padding: 0,
                                    color: '#2563eb', cursor: 'pointer',
                                    fontWeight: 500, fontSize: 'inherit',
                                }}
                                title="Show full mention text">
                                Show more
                            </button>
                        </>
                    )}
                    {bodyExpanded && ((showTranslated && translation) ? translation : (m.text || '')).length > BODY_PREVIEW_LEN && (
                        <>
                            {' '}
                            <button type="button"
                                onClick={() => setBodyExpanded(false)}
                                style={{
                                    background: 'none', border: 'none', padding: 0,
                                    color: '#64748b', cursor: 'pointer',
                                    fontWeight: 500, fontSize: 'inherit',
                                }}>
                                Show less
                            </button>
                        </>
                    )}
                </div>
                {canTranslate && (
                    <div style={{
                        marginTop: 6, display: 'flex', gap: 8, alignItems: 'center',
                        fontSize: 11, color: '#64748b',
                    }}>
                        <CommonButton
                            text={translating
                                ? '⏳ Translating…'
                                : (showTranslated
                                    ? `✓ English (from ${lang.toUpperCase()})`
                                    : `🌐 Translate from ${lang.toUpperCase()}`)}
                            variant="primary"
                            className="bm_translate_btn"
                            onClick={toggleTranslate}
                            disable={translating}
                            title={`Source language: ${lang.toUpperCase()}. FORESEE AI translates to English on demand; result is cached.`}
                        />
                        {translation && showTranslated && (
                            <span style={{ fontSize: 10, color: '#94a3b8' }}>
                                FORESEE AI
                            </span>
                        )}
                        {translateError && (
                            <span style={{ color: '#b91c1c', fontSize: 11 }}>
                                {translateError}
                            </span>
                        )}
                    </div>
                )}
                {/* {m.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer"
                       className="bm_card_link"
                       onClick={() => { if (!m.is_visited) toggleVisited(); }}>
                        {m.url}
                    </a>
                )} */}
                {/* Applied tag chips (rendered below the body text) */}
                {(m.tags || []).length > 0 && (
                    <div className="bm_card_tagchips">
                        {(m.tags || []).map(t => {
                            const c = tagColorFor(t, m.id);
                            return (
                                <span key={t.id} className="bm_card_tagchip"
                                    style={{ background: c + '18', color: c, borderColor: c + '60' }}
                                    title={`Tag · ${t.name} · click × to remove`}>
                                    {t.name}
                                    <button type="button"
                                        onClick={() => toggleTag(t)} disabled={busy}
                                        aria-label={`Remove tag ${t.name}`}
                                        style={{
                                            background: 'transparent', border: 'none',
                                            color: 'inherit', cursor: 'pointer',
                                            padding: '0 0 0 6px', fontSize: 12
                                        }}>
                                        ×
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* 2026-05-27 UX cleanup - split actions into 3 primary
                    inline + "More" popover for the rest. Primary set
                    chosen for triage flow: Mark read, Review, Takedown. */}
                <div className="bm_card_toolbar">
                    <CommonButton
                        text={m.is_visited ? '● Read' : '○ Mark read'}
                        variant={m.is_visited ? 'secondary' : 'primary'}
                        className="bm_card_tool"
                        onClick={toggleVisited} disable={busy}
                        title={m.is_visited ? 'Mark as unread' : 'Mark as read'}
                    />

                    {m.url && (
                        <CommonButton
                            text={`↗ Review on ${fmtPlatform(m.platform)}`}
                            variant="secondary"
                            className="bm_card_tool"
                            title="Open the post on the source platform in a new tab"
                            onClick={() => {
                                window.open(m.url, '_blank', 'noopener,noreferrer');
                                if (!m.is_visited) toggleVisited();
                            }}
                        />
                    )}
                    {COMMENT_PLATFORMS.includes(m.platform) && (
                        <CommonButton
                            text="Show Comments"
                            variant="secondary"
                            className="bm_card_tool"
                            onClick={() => setCommentsModalOpen(true)}
                            disable={busy}
                            title="View comments on this post"
                        />
                    )}
                    {['x'].includes(m.platform) && (
                        <CommonButton
                            text={m?.retweet_count > 1 ? `Retweets: ${m.retweet_count}` : `Retweet: ${m.retweet_count}`}
                            variant="white"
                            className="bm_card_tool"
                            disable
                        />
                    )}
                    {m.url && (
                        <CommonButton
                            text={takedownActive ? '⏱ Takedown · ' + takedown.status : '⚠ Request takedown'}
                            variant={takedownActive ? 'secondary' : 'error'}
                            className="bm_card_tool"
                            onClick={() => setTakedownModalOpen(true)}
                            disable={takedown?.status === 'requested'}
                            title={takedownActive
                                ? `Takedown already in flight (status: ${takedown.status}). Click to view.`
                                : 'Request a takedown of this post / host'}
                        />
                    )}
                    <div style={{ position: 'relative', marginLeft: 'auto' }}>
                        <CommonButton
                            text="⋯ More"
                            variant="white"
                            className="bm_card_tool"
                            onClick={() => setMoreOpen(o => !o)}
                            disable={busy}
                            title="More actions"
                            aria-haspopup="true"
                            aria-expanded={moreOpen}
                        />
                        {moreOpen && (
                            <div onMouseLeave={() => setMoreOpen(false)}
                                style={{
                                    position: 'absolute', right: 0, top: 'calc(100% + 4px)',
                                    minWidth: 220, zIndex: 10,
                                    background: '#ffffff',
                                    border: '1px solid rgba(148,163,184,0.45)',
                                    borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(15,23,42,0.10)',
                                    padding: 6,
                                    display: 'flex', flexDirection: 'column', gap: 2,
                                }}>
                                {/* Tag picker - inline sub-menu */}
                                <button type="button"
                                    onClick={() => setTagPickerOpen(o => !o)}
                                    disabled={busy}
                                    style={menuItemStyle}>
                                    {tagPickerOpen ? '▾ Tag' : '+ Tag'}
                                </button>
                                {tagPickerOpen && (
                                    <div className="bm_card_tag_panel"
                                        style={{ position: 'static', marginTop: 0, marginBottom: 4 }}>
                                        {orgTags.length > 0 && (
                                            <div className="bm_card_tag_list">
                                                {orgTags.map(t => {
                                                    const on = appliedTagIds.has(t.id);
                                                    const c = tagColorFor(t, t.id);
                                                    return (
                                                        <button key={t.id}
                                                            type="button"
                                                            className={`bm_card_tag_opt ${on ? 'on' : ''}`}
                                                            onClick={() => toggleTag(t)}
                                                            disabled={busy}>
                                                            <span style={{
                                                                display: 'inline-block',
                                                                width: 8, height: 8, borderRadius: 2,
                                                                background: c, marginRight: 6
                                                            }} />
                                                            {t.name}
                                                            {on && <span style={{ marginLeft: 'auto' }}>✓</span>}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <div className="bm_card_tag_new">
                                            <input type="text" placeholder="New tag name…"
                                                value={newTagName}
                                                onChange={e => setNewTagName(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') createAndApply(); }} />
                                            <CommonButton
                                                text="Add"
                                                variant="primary"
                                                className="bm_scan_btn"
                                                onClick={createAndApply}
                                                disable={busy || !newTagName.trim()}
                                            />
                                        </div>
                                    </div>
                                )}
                                <button type="button"
                                    onClick={() => { toggleQueue(); setMoreOpen(false); }}
                                    disabled={busy}
                                    style={menuItemStyle}
                                    title={m.is_in_report_queue
                                        ? 'Remove from report queue'
                                        : 'Add to the next PDF/Excel/CSV report'}>
                                    {m.is_in_report_queue ? '✓ In report' : '+ Add to report'}
                                </button>
                                {/* {host && (
                                    <button type="button"
                                            onClick={() => { muteHost(); setMoreOpen(false); }}
                                            disabled={busy}
                                            style={menuItemStyle}
                                            title={`Mute every future mention from ${host}`}>
                                        🔇 Mute {host}
                                    </button>
                                )} */}
                            </div>
                        )}
                    </div>
                </div>
                {takedownModalOpen && (
                    <TakedownModal
                        mention={m}
                        onClose={() => setTakedownModalOpen(false)}
                        onSubmitted={(mid, td) => {
                            onTakedown && onTakedown(mid, td);
                        }} />
                )}
                {commentsModalOpen && (
                    <CommentsModal
                        mention={m}
                        onClose={() => setCommentsModalOpen(false)} />
                )}
            </div>
        </div>
    );
}

export default function MentionsTab() {
    const { confirm, confirmModal } = useConfirm();
    const LS_KEY = 'bm_saved_filter_v1';
    const [days, setDays] = useState(30);
    const [bucket, setBucket] = useState('days');
    const [chartMode, setChartMode] = useState('mentions_reach');
    const [platforms, setPlatforms] = useState([]);
    const [sentiments, setSentiments] = useState([]);
    const [riskOnly, setRiskOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sort, setSort] = useState('-posted_at');
    const [refreshTick, setRefreshTick] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    // 2026-05-26 - Scan now (dispatches brand_mention_scan) + status
    // indicator showing the customer their pending edits aren't live
    // until the next scan. Refresh ≠ Scan: Refresh re-fetches the
    // mentions API; Scan re-runs the underlying brand_mention_scan task
    // that hits Reddit / X / Facebook / LinkedIn / etc.
    const [scanBusy, setScanBusy] = useState(false);
    const [scanRunning, setScanRunning] = useState(false);
    const { notify, toastNode } = useToast();
    const [scanStatus, setScanStatus] = useState(null);
    // Poll handle for the scan-status loop kicked off by "Scan now".
    // Cleared when a newer last_scan_at lands, on safety timeout, or unmount.
    const scanPollRef = useRef(null);
    const scanPollTimeoutRef = useRef(null);
    // CEPI RFP #28 - alert-latency SLA pill (p95 + 24h SLA compliance over
    // the last 30 days). Lives in the header next to the Monitoring pill.
    const [alertLatency, setAlertLatency] = useState(null);
    // New-since-last-view delta - pulled from localStorage on mount,
    // re-pulled after Scan now / Refresh, written when the user closes
    // the tab or scrolls past page 1 (treating that as "they engaged").
    const [newSinceCount, setNewSinceCount] = useState(0);
    const [savedSnapshot, setSavedSnapshot] = useState(null);
    // Brand24-parity slice 2 - Visited filter (all/read/unread).
    const [visitedMode, setVisitedMode] = useState('all');
    // Brand24-parity slice 2 - Tag taxonomy + active tag filter (IDs).
    const [orgTags, setOrgTags] = useState([]);
    const [activeTagIds, setActiveTagIds] = useState([]);
    // Brand24-parity slice 2-C - PDF report queue.
    const [queueCount, setQueueCount] = useState(0);
    const [showQueueOnly, setShowQueueOnly] = useState(false);
    // Brand24-parity slice 3 - right-rail filters.
    const [importanceMode, setImportanceMode] = useState('all'); // all|important
    const [activeLanguages, setActiveLanguages] = useState([]);
    // Spike drill-in (slice 3-D) - selected marker + date-bucket filter.
    const [selectedSpike, setSelectedSpike] = useState(null);
    const [bucketFilter, setBucketFilter] = useState({ from: '', to: '' });
    // Topic card drill-in (slice 3-E) - when an analyst clicks a card on
    // Topic Analysis, that tab fires `bm-mentions-set-filter` with either
    // {narrative} (auto-detected) or {monitored_topic} (user-defined).
    const [monitoredTopic, setMonitoredTopic] = useState('');
    const [narrativeFilter, setNarrativeFilter] = useState('');
    // Influencer/Sources drill-in (slice 3-F) - the author handle an analyst
    // clicked on the Influencers & Sources tab. Sent to the backend as BOTH:
    //   • `author=` - the exact-match filter the backend is expected to add.
    //     Until it does, the backend silently ignores this param (harmless).
    //   • folded into `search=` - the fuzzy fallback that works TODAY (the
    //     search index already covers authors), so the feed still narrows
    //     before the backend change ships. Once `author=` is live it takes
    //     over and the search fold-in becomes a redundant no-op (an author's
    //     own rows match their handle anyway), giving an exact result.
    const [authorFilter, setAuthorFilter] = useState('');

    // Hydrate from localStorage if a saved filter set exists.
    useEffect(() => {
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
                const f = JSON.parse(raw);
                setSavedSnapshot(f);
            }
        } catch (_) { /* ignore */ }
    }, []);

    // Topic card drill-in listener - payload is {narrative} or
    // {monitored_topic}. Setting either updates filterParams via state.
    useEffect(() => {
        const handler = (e) => {
            const d = e.detail || {};
            // Clear other potentially-conflicting filters so the analyst
            // sees exactly the topic/author/platform's mentions, not the
            // intersection with leftover state from the previous filter.
            if (d.narrative) {
                setNarrativeFilter(d.narrative);
                setMonitoredTopic('');
            }
            if (d.monitored_topic) {
                setMonitoredTopic(d.monitored_topic);
                setNarrativeFilter('');
            }
            // Influencer/Sources drill-in (slice 3-F). Store the handle in
            // the dedicated `authorFilter` (see its declaration for how it
            // maps to `author=` + `search=`). Clear any typed search so the
            // author view is clean - the "Feed filtered to author" banner is
            // the visible indicator, alongside the toolbar "Clear filters".
            if (d.author) {
                setAuthorFilter(d.author);
                setSearch('');
            }
            if (d.platform) {
                setPlatforms([(d.platform || '').toLowerCase()]);
            }
            setBucketFilter({ from: '', to: '' });
            setPage(1);
        };
        window.addEventListener('bm-mentions-set-filter', handler);
        return () => window.removeEventListener('bm-mentions-set-filter', handler);
    }, []);

    const filtersAreCustom = (
        platforms.length > 0 || sentiments.length > 0 ||
        riskOnly || search.length > 0 || days !== 30 || sort !== '-posted_at' ||
        activeLanguages.length > 0 || activeTagIds.length > 0 ||
        showQueueOnly || visitedMode !== 'all' || importanceMode !== 'all' ||
        !!bucketFilter.from || !!bucketFilter.to ||
        !!monitoredTopic || !!narrativeFilter || !!authorFilter
    );

    const clearFilters = () => {
        setPlatforms([]); setSentiments([]); setRiskOnly(false);
        setSearch(''); setDays(30); setSort('-posted_at'); setPage(1);
        setActiveLanguages([]);
        setActiveTagIds([]); setShowQueueOnly(false);
        setVisitedMode('all'); setImportanceMode('all');
        setBucketFilter({ from: '', to: '' });
        setMonitoredTopic(''); setNarrativeFilter(''); setAuthorFilter('');
    };

    const loadSavedFilters = () => {
        if (!savedSnapshot) return;
        setDays(savedSnapshot.days ?? 30);
        setPlatforms(savedSnapshot.platforms || []);
        setSentiments(savedSnapshot.sentiments || []);
        setRiskOnly(!!savedSnapshot.riskOnly);
        setSearch(savedSnapshot.search || '');
        setSort(savedSnapshot.sort || '-posted_at');
        setActiveLanguages(savedSnapshot.activeLanguages || []);
        setActiveTagIds(savedSnapshot.activeTagIds || []);
        setShowQueueOnly(!!savedSnapshot.showQueueOnly);
        setVisitedMode(savedSnapshot.visitedMode || 'all');
        setImportanceMode(savedSnapshot.importanceMode || 'all');
        setBucketFilter(savedSnapshot.bucketFilter || { from: '', to: '' });
        setMonitoredTopic(savedSnapshot.monitoredTopic || '');
        setNarrativeFilter(savedSnapshot.narrativeFilter || '');
        setPage(1);
    };

    const forgetSaved = () => {
        try { localStorage.removeItem(LS_KEY); } catch (_) { }
        setSavedSnapshot(null);
    };

    const [feed, setFeed] = useState({ results: [], total: 0, pages: 0 });
    const feedRef = useRef(null);
    const [feedLoading, setFeedLoading] = useState(false);
    const [chart, setChart] = useState({ mentions_reach_series: [], sentiment_series: [] });
    const [influencers, setInfluencers] = useState([]);
    const [langModalOpen, setLangModalOpen] = useState(false);
    const [filterOptions, setFilterOptions] = useState({ platforms: [], sentiments: [] });
    console.log("foooo", filterOptions);

    const [undiscovered, setUndiscovered] = useState({ items: [], dismissed: false });
    const [aiPanel, setAiPanel] = useState(null);   // {state, text} | null

    const filterParams = useMemo(() => ({
        days,
        platform: platforms.join(',') || undefined,
        sentiment: sentiments.join(',') || undefined,
        risk_only: riskOnly ? 1 : undefined,
        // Author drill-in folds into `search` as the fallback that works
        // today; a typed search takes precedence if both are somehow set.
        search: (search || authorFilter) || undefined,
        // Exact author filter - a no-op until the backend adds it, then it
        // supersedes the `search` fold-in above. See `authorFilter` decl.
        author: authorFilter || undefined,
        visited: visitedMode === 'all' ? undefined : visitedMode,
        tag_ids: activeTagIds.length ? activeTagIds.join(',') : undefined,
        from_queue: showQueueOnly ? 1 : undefined,
        importance: importanceMode === 'all' ? undefined : importanceMode,
        language: activeLanguages.length ? activeLanguages.join(',') : undefined,
        narrative: narrativeFilter || undefined,
        from_date: bucketFilter.from || undefined,
        to_date: bucketFilter.to || undefined,
        monitored_topic: monitoredTopic || undefined,
    }), [days, platforms, sentiments, riskOnly, search, authorFilter, visitedMode, activeTagIds, showQueueOnly,
        importanceMode, activeLanguages,
        bucketFilter, monitoredTopic, narrativeFilter]);
    const { data: languageData } = useLanguageData(filterParams);
    const toggleLanguage = l => {
        setActiveLanguages(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
        setPage(1);
    };

    const refreshOrgTags = () => {
        bp_list_mention_tags()
            .then(r => setOrgTags(((unwrap(r) || {}).items) || []))
            .catch(() => { });
    };
    const deleteOrgTag = async (tag) => {
        const ok = await confirm({
            title: 'Delete tag',
            message: `Delete tag "${tag.name}"? It will be removed from every mention that currently has it.`,
            confirmLabel: 'Delete',
            danger: true,
            themeColor: false,
        });
        if (!ok) return;
        try {
            await bp_delete_mention_tag(tag.id);
        } catch (_) {
            window.alert('Could not delete tag. Try again.');
            return;
        }
        setOrgTags(prev => prev.filter(t => t.id !== tag.id));
        setActiveTagIds(prev => prev.filter(id => id !== tag.id));
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r => ({
                ...r,
                tags: (r.tags || []).filter(x => x.id !== tag.id),
            })),
        }));
    };
    const refreshQueueCount = () => {
        bp_list_report_queue()
            .then(r => setQueueCount(((unwrap(r) || {}).total) || 0))
            .catch(() => { });
    };

    // Clear any in-flight scan-status polling on unmount.
    useEffect(() => () => {
        if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null; }
        if (scanPollTimeoutRef.current) { clearTimeout(scanPollTimeoutRef.current); scanPollTimeoutRef.current = null; }
    }, []);

    useEffect(() => {
        bp_filters().then(res => setFilterOptions(unwrap(res) || {})).catch(() => { });
        refreshOrgTags();
        refreshQueueCount();
        bp_scan_status().then(res => setScanStatus(unwrap(res) || null)).catch(() => { });
        bp_alert_latency({ days: 30 })
            .then(res => setAlertLatency(unwrap(res) || null))
            .catch(() => { });
        // New-since-last-view: read the stored timestamp (per-org) and
        // ask the backend how many rows landed after it. Don't overwrite
        // the stored timestamp until the user explicitly engages with
        // the feed - that way someone bouncing between tabs all day
        // still sees "12 new" until they actually look.
        try {
            const orgId = localStorage.getItem('organization_id') || '0';
            const key = `bm_last_viewed_at_org_${orgId}`;
            const since = localStorage.getItem(key);
            if (since) {
                bp_mentions_since({ since })
                    .then(res => {
                        const d = unwrap(res) || {};
                        setNewSinceCount(d.count || 0);
                    })
                    .catch(() => { });
            } else {
                // First visit ever - seed the timestamp to now, no delta
                // to show yet.
                localStorage.setItem(key, new Date().toISOString());
            }
        } catch { }
        // Brand24-parity: surface providers NMT has wired up but this
        // tenant isn't entitled to yet. Backend already excludes anything
        // the org has access to, so a non-empty list = legitimate gap.
        const dismissed = localStorage.getItem('bm_undiscovered_dismiss') === '1';
        if (!dismissed) {
            bp_undiscovered_sources()
                .then(res => {
                    const body = unwrap(res) || {};
                    setUndiscovered({
                        items: body.items || [],
                        contact_email: body.contact_email,
                        dismissed: false,
                    });
                })
                .catch(() => { });
        } else {
            setUndiscovered({ items: [], dismissed: true });
        }
    }, []);

    const dismissUndiscovered = () => {
        try { localStorage.setItem('bm_undiscovered_dismiss', '1'); } catch (_) { }
        setUndiscovered(u => ({ ...u, dismissed: true }));
    };

    const runSummarize = async () => {
    setAiPanel({ state: 'loading', text: '' });
    try {
        const res = await bp_assistant_ask({
            question: '',
            thread: [],
            mode: 'summary',
            days,
        });
        const data = unwrap(res) || {};
        setAiPanel({
            state: 'done',
            text: data.answer || 'No summary returned.',
            provider: data.provider
        });
    } catch (e) {
        setAiPanel({
            state: 'error',
            text: 'AI summary is temporarily unavailable. Try again in a moment.'
        });
    }
};


    useEffect(() => {
        const t = setTimeout(() => {
            setFeedLoading(true);
            Promise.allSettled([
                bp_mentions({ ...filterParams, page, sort, page_size: pageSize }),
                bp_chart({ ...filterParams, bucket }),
                bp_influencers({ ...filterParams, limit: 10 }),
            ]).finally(() => { setFeedLoading(false); setRefreshing(false); }).then(([m, c, i]) => {
                const ok = r => r?.status === 'fulfilled' ? r.value : null;
                [m, c, i] = [ok(m), ok(c), ok(i)];
                setFeed(unwrap(m));
                // Clamp the chart series to the days window: the backend filters on created_at but
                // charts bucket by posted_at, so old archived posts stretch the x-axis. The cutoff
                // snaps to the start of its week/month so the boundary bucket is not dropped.
                const chartData = unwrap(c) || {};
                let cutoffMs = Date.now() - (days * 86400 * 1000);
                if (bucket === 'months') {
                    const c0 = new Date(cutoffMs);
                    cutoffMs = Date.UTC(c0.getUTCFullYear(), c0.getUTCMonth(), 1);
                } else if (bucket === 'weeks') {
                    const c0 = new Date(cutoffMs);
                    const isoMon = (c0.getUTCDay() + 6) % 7;
                    cutoffMs = Date.UTC(c0.getUTCFullYear(), c0.getUTCMonth(), c0.getUTCDate()) - isoMon * 86400000;
                }
                const inRange = (b) => {
                    const t = bucketStartMs(b && b.bucket);
                    return Number.isFinite(t) && t >= cutoffMs;
                };
                setChart({
                    ...chartData,
                    mentions_reach_series: (chartData.mentions_reach_series || []).filter(inRange),
                    sentiment_series: (chartData.sentiment_series || []).filter(inRange),
                });
                setInfluencers((unwrap(i) || {}).influencers || []);
            });
        }, search ? 300 : 0);
        return () => clearTimeout(t);
    }, [filterParams, page, pageSize, sort, bucket, refreshTick]);

    const toggleInList = (val, list, setter) => {
        const v = val.toLowerCase();
        setter(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
        setPage(1);
    };

    // Brand24-parity slice 2 - local handlers so cards can update visited /
    // muted state without re-fetching the whole feed page.
    const handleVisitChange = (mentionId, nextVisited) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, is_visited: nextVisited } : r
            ),
        }));
    };
    const handleVerdictChange = (mentionId, verdict) => {
        // Don't mutate is_risk_tagged_source - that's the backend's
        // immutable signal that the source itself was flagged. The risk
        // badge is hidden/shown based on customer_verdict in the render
        // path, which means undoing a verdict back to 'unreviewed' brings
        // the panel and the RISK SOURCE chip back as expected.
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, customer_verdict: verdict } : r
            ),
        }));
    };
    const handleTakedownSubmitted = (mentionId, td) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, takedown: td } : r
            ),
        }));
    };
    const handleQueueChange = (mentionId, nextQueued) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, is_in_report_queue: nextQueued } : r
            ),
        }));
        setQueueCount(c => Math.max(0, c + (nextQueued ? 1 : -1)));
    };
    const clearQueue = async () => {
        if (!queueCount) return;
        const ok = await confirm({
            title: 'Clear report queue',
            message: `Clear all ${queueCount} queued mentions? This empties the report queue but does not delete the mentions themselves.`,
            confirmLabel: 'Clear',
            danger: true,
            themeColor: false,
        });
        if (!ok) return;
        await bp_clear_report_queue();
        setQueueCount(0);
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r => ({ ...r, is_in_report_queue: false })),
        }));
    };
    const handleTagsChange = (mentionId, nextTags) => {
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).map(r =>
                r.id === mentionId ? { ...r, tags: nextTags } : r
            ),
        }));
        // Optimistically merge any newly-created tag into the org-tag list
        // so the toolbar chip appears immediately. refreshOrgTags() below
        // is the eventual-consistency safety net.
        setOrgTags(prev => {
            const known = new Set(prev.map(t => t.id));
            const additions = (nextTags || []).filter(t => t && t.id && !known.has(t.id));
            return additions.length ? [...prev, ...additions] : prev;
        });
        refreshOrgTags();
    };
    const handleMuteHost = (host) => {
        const sameHost = (url) => {
            try { return new URL(url).hostname.replace(/^www\./, '') === host; }
            catch (_) { return false; }
        };
        setFeed(prev => ({
            ...prev,
            results: (prev.results || []).filter(r => !sameHost(r.url)),
            total: Math.max(0, (prev.total || 0)
                - (prev.results || []).filter(r => sameHost(r.url)).length),
        }));
    };

    const SCAN_NOT_STARTED = { title: 'Scan not started', duration: 8000 };
    const handleScanNow = async () => {
        setScanBusy(true);
        const baselineScanAt = scanStatus?.last_scan_at || null;
        try {
            const res = await bp_rescan_mentions();
            const sc = res?.statusCode || res || {};
            if (sc.status === 0 && sc.error === 'rate_limited') {
                notify(formatRateLimitedMessage(sc.message, scanStatus?.cadence_minutes), 'warning', SCAN_NOT_STARTED);
            } else {
                const d = (sc?.response_data?.data || sc?.data || res?.data || {});
                if (!d.dispatched) {
                    notify('The scan was acknowledged but not dispatched. Check the server logs.', 'warning', SCAN_NOT_STARTED);
                } else {
                    notify('New mentions will appear in a few minutes.', 'success', { title: 'Scan started' });
                    setScanRunning(true);
                    if (scanPollRef.current) clearInterval(scanPollRef.current);
                    if (scanPollTimeoutRef.current) clearTimeout(scanPollTimeoutRef.current);
                    const stopPolling = () => {
                        if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null; }
                        if (scanPollTimeoutRef.current) { clearTimeout(scanPollTimeoutRef.current); scanPollTimeoutRef.current = null; }
                        setScanRunning(false);
                    };
                    // `finished`, not the refs, decides whether to keep polling:
                    // stopPolling() nulls them when the first check is already done.
                    let finished = false;
                    const pollOnce = async () => {
                        try {
                            const sres = await bp_scan_status();
                            const sdata = unwrap(sres) || null;
                            setScanStatus(sdata);
                            if (!finished && sdata && sdata.last_scan_at && sdata.last_scan_at !== baselineScanAt) {
                                finished = true;
                                stopPolling();
                                setRefreshTick(t => t + 1);
                                notify('Mentions refreshed.', 'success', { title: 'Scan complete' });
                            }
                        } catch (_) { /* ignore, keep polling */ }
                    };
                    await pollOnce();
                    if (!finished) {
                        scanPollRef.current = setInterval(pollOnce, 2 * 60 * 1000);
                        scanPollTimeoutRef.current = setTimeout(() => {
                            stopPolling();
                            notify('The scan is taking longer than usual. Use Refresh later to load its results.', 'info',
                                { title: 'Still scanning', duration: 8000 });
                        }, 30 * 60 * 1000);
                    }
                }
            }
        } catch (err) {
            const data = err?.response?.data || {};
            if (data.error === 'rate_limited') {
                notify(formatRateLimitedMessage(data?.message, scanStatus?.cadence_minutes), 'warning', SCAN_NOT_STARTED);
            } else {
                notify(`Could not start the scan: ${data.error || err?.message || 'failed'}`, 'error',
                    { title: 'Scan failed', duration: 8000 });
            }
        } finally {
            setScanBusy(false);
        }
    };

    return (
        <>
            <FullPageLoader loading={refreshing} />
            {confirmModal}
            {toastNode}
            <div className="bm_feed_controls">
                <div className="bm_feed_search">
                    <CommonSearchBar value={search} placeholder="Search through mentions & authors" showClear={true} onChange={val => { setSearch(val); setPage(1); }} />
                </div>
                <CommonDropdown
                    ariaLabel="Date range"
                    value={days}
                    onChange={(v) => { setDays(v); setPage(1); }}
                    options={DAYS_OPTIONS}
                />
                <CommonDropdown
                    ariaLabel="Sort"
                    value={sort}
                    onChange={(v) => { setSort(v); setPage(1); }}
                    options={SORT_OPTIONS}
                />
                {queueCount > 0 && (
                    <CommonButton
                        text={
                            <>
                                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                                    <rect x="3" y="3" width="10" height="10" rx="1.5"
                                        fill="none" stroke="currentColor" strokeWidth="1.4" />
                                    <polyline points="5.5,8 7.5,10 10.5,6"
                                        fill="none" stroke="currentColor" strokeWidth="1.4"
                                        strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Report queue · {queueCount}
                            </>
                        }
                        variant={showQueueOnly ? 'primary' : 'white'}
                        className="bm_bar_btn"
                        onClick={() => { setShowQueueOnly(v => !v); setPage(1); }}
                        title={showQueueOnly
                            ? 'Showing only queued mentions. Click to see all'
                            : 'Show only mentions in your report queue'}
                    />
                )}
                {queueCount > 0 && (
                    <CommonButton
                        text="Clear"
                        variant="white"
                        className="bm_bar_btn"
                        onClick={clearQueue}
                        title="Empty the queue"
                    />
                )}
                <div className="bm_ai_actions">
                    <CommonButton
                        text={
                            <>
                                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                                    <circle cx="8" cy="8" r="6.5" fill="none"
                                        stroke="currentColor" strokeWidth="1.4" />
                                    <circle cx="8" cy="8" r="2.2" fill="currentColor" />
                                </svg>
                                {aiPanel?.state === 'loading' ? 'Summarising…' : 'Summarize with FORESEE AI'}
                            </>
                        }
                        onClick={runSummarize}
                        variant={aiPanel?.state === 'loading' ? 'secondary' : 'primary'}
                        disable={aiPanel?.state === 'loading'}
                        className="bm_bar_btn"
                    />
                </div>
            </div>

            <div className="bm_feed_result">
                <div className="bm_feed_result_count">
                    <SubHeading as="span" variant={3} className="bm_feed_result_total"
                        title={typeof feed.total_all_time === 'number' && feed.total_all_time === feed.total && feed.total > 0
                            ? `Every captured mention for this org fits inside the last ${days} days, so widening the date range won't pull more in.`
                            : undefined}>
                        {formatNumber(feed.total || 0)} {feed.total === 1 ? 'mention' : 'mentions'}
                    </SubHeading>
                    {typeof feed.total_all_time === 'number' && feed.total_all_time !== feed.total && (
                        <Body as="span" variant={2} className="bm_feed_result_muted">
                            · {formatNumber(feed.total_all_time)} captured all-time
                        </Body>
                    )}
                </div>
                <div className="bm_feed_result_status">
                    {newSinceCount > 0 && (
                        <CommonButton
                            text={`${newSinceCount} new since you last viewed →`}
                            variant="se"
                            className="bm_newsince_btn"
                            onClick={() => {
                                try {
                                    const orgId = localStorage.getItem('organization_id') || '0';
                                    localStorage.setItem(`bm_last_viewed_at_org_${orgId}`, new Date().toISOString());
                                } catch { }
                                setNewSinceCount(0);
                                setRefreshTick(t => t + 1);
                            }}
                            title="Mark as viewed and refresh the feed"
                        />
                    )}
                    {scanRunning ? (
                        <StatusBadge variant="info" pulse transform="none"
                            title="A manual scan is running. The feed refreshes when it finishes.">
                            Scan in progress
                        </StatusBadge>
                    ) : scanStatus && scanStatus.last_scan_at && (
                        <StatusBadge variant={scanStatus.manual_only ? 'neutral' : 'success'}
                            pulse={!scanStatus.manual_only}
                            transform="none"
                            title={`Last scan: ${formatLocalDateTime(scanStatus.last_scan_at)}`}>
                            {scanStatus.manual_only ? 'Manual only' : 'Monitoring'} · updated {timeAgo(scanStatus.last_scan_at)}
                        </StatusBadge>
                    )}
                    {savedSnapshot && (
                        <>
                            <CommonButton
                                text="Apply saved"
                                variant="white"
                                className="bm_bar_btn bm_bar_btn--sm"
                                onClick={loadSavedFilters}
                                title="Apply your saved filter set"
                            />
                            <button className="bm_chip" onClick={forgetSaved}
                                title="Forget the saved filter set">×</button>
                        </>
                    )}
                    <CommonButton
                        text="Refresh"
                        variant="white"
                        className="bm_bar_btn bm_bar_btn--sm"
                        onClick={() => { setRefreshing(true); setRefreshTick(t => t + 1); }}
                        disable={refreshing}
                        title="Reload the feed"
                    />
                    <CommonButton
                        text={scanBusy || scanRunning ? 'Scanning…' : 'Scan now'}
                        variant="white"
                        className="bm_bar_btn bm_bar_btn--sm"
                        onClick={handleScanNow}
                        disable={scanBusy || scanRunning}
                        title="Start a new scan of your sources"
                    />
                </div>
            </div>

            {orgTags.length > 0 && (
                <div className="bm_feed_tags">
                    {/* Tag filter chips - light up active tags; click toggles. */}
                    {orgTags.map(t => {
                        const on = activeTagIds.includes(t.id);
                        const c = tagColorFor(t, t.id);
                        return (
                            <span key={'tag-' + t.id}
                                className={`bm_chip bm_chip_tag ${on ? 'active' : ''}`}
                                style={on
                                    ? {
                                        borderColor: c, color: c, background: c + '12',
                                        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 6px 3px 10px'
                                    }
                                    : {
                                        borderColor: c + '60', color: c,
                                        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 6px 3px 10px'
                                    }}>
                                <button type="button"
                                    onClick={() => {
                                        setActiveTagIds(prev => prev.includes(t.id)
                                            ? prev.filter(x => x !== t.id)
                                            : [...prev, t.id]);
                                        setPage(1);
                                    }}
                                    style={{
                                        background: 'transparent', border: 'none',
                                        color: 'inherit', cursor: 'pointer', padding: 0,
                                        font: 'inherit'
                                    }}
                                    title={on ? `Click to remove "${t.name}" filter` : `Click to filter by "${t.name}"`}>
                                    {on ? '● ' : ''}{t.name}
                                </button>
                                <button type="button"
                                    onClick={(e) => { e.stopPropagation(); deleteOrgTag(t); }}
                                    style={{
                                        background: 'transparent', border: 'none',
                                        color: 'inherit', cursor: 'pointer',
                                        padding: '0 2px', fontSize: 13, lineHeight: 1,
                                        opacity: 0.7
                                    }}
                                    aria-label={`Delete tag ${t.name}`}
                                    title={`Delete tag "${t.name}" (removes it from every mention org-wide)`}>
                                    ×
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            {undiscovered.items.length > 0 && !undiscovered.dismissed && (
                <div className="bm_upsell">
                    <div className="bm_upsell_body">
                        <strong>You have undiscovered mentions on{' '}
                            {undiscovered.items.length === 1
                                ? undiscovered.items[0].label
                                : `${undiscovered.items.length} additional sources`}.
                        </strong>
                        <p>
                            {undiscovered.items.length === 1
                                ? undiscovered.items[0].blurb + '. '
                                : 'Activate them to extend mention coverage across '
                                + undiscovered.items.map(i => i.label).join(' · ') + '. '}
                            Your current contract doesn’t include{' '}
                            {undiscovered.items.length === 1 ? 'this source' : 'these sources'} yet.
                        </p>
                    </div>
                    <div className="bm_upsell_actions">
                        <a className="bm_scan_btn cmn_btn"
                            href={`mailto:${undiscovered.contact_email || 'sales@foreseesecurity.io'}`
                                + `?subject=Request%20access%20-%20${encodeURIComponent(
                                    undiscovered.items.map(i => i.label).join(', '))}`
                                + `&body=${encodeURIComponent('Hi NMT, please enable the following data sources for our tenant:\n\n'
                                    + undiscovered.items.map(i => '• ' + i.label).join('\n'))}`}>
                            Request access
                        </a>
                        <CommonButton
                            text="Hide"
                            variant="white"
                            className="bm_upsell_dismiss"
                            onClick={dismissUndiscovered}
                            title="Hide this message in this browser"
                        />
                    </div>
                </div>
            )}

            {aiPanel && (
                <div className="bm_ai_panel cmn_elevated_card">
                    <div className="bm_ai_panel_head">
                        <img  src={foreseeLogo} alt="" width={32} height={32} />
                        <div className="bm_ai_panel_titles">
                            <SubHeading as="h3" variant={3} className="bm_ai_panel_title">FORESEE AI Summary</SubHeading>
                            <Body as="span" variant={2} className="bm_ai_panel_sub">
                                {DAYS_OPTIONS.find(o => o.value === days)?.label || `Last ${days} days`}
                            </Body>
                        </div>
                        <button type="button" className="bm_ai_panel_close"
                            onClick={() => setAiPanel(null)}
                            aria-label="Close AI summary" title="Close">×</button>
                    </div>
                    <div className="bm_ai_panel_body">
                        {aiPanel.state === 'loading' && (
                            <div className="bm_ai_panel_loading">
                                <Skeleton height={12} width="90%" />
                                <Skeleton height={12} width="75%" />
                                <Skeleton height={12} width="82%" />
                            </div>
                        )}
                        {aiPanel.state === 'error' && (
                            <Body className="bm_ai_panel_error">{aiPanel.text}</Body>
                        )}
                        {aiPanel.state === 'done' && (
                            <div className="bm_md"><ReactMarkdown>{mdNormalize(aiPanel.text)}</ReactMarkdown></div>
                        )}
                    </div>
                    {aiPanel.state === 'done' && (
                        <Body as="div" variant={2} className="bm_ai_panel_footer">
                            Generated by FORESEE AI from your scan data
                        </Body>
                    )}
                </div>
            )}

            <div className="bm_grid">
                <div className="bm_main">
                    <div className="bm_chart_card cmn_elevated_card">
                        <div className="bm_chart_toolbar">
                            <CommonTabs
                                tabs={['Mentions', 'Sentiment']}
                                values={['mentions_reach', 'sentiment']}
                                active={chartMode}
                                handlers={[() => setChartMode('mentions_reach'), () => setChartMode('sentiment')]}
                            />
                            <CommonTabs
                                tabs={['Days', 'Weeks', 'Months']}
                                values={['days', 'weeks', 'months']}
                                active={bucket}
                                handlers={['days', 'weeks', 'months'].map(b => () => setBucket(b))}
                            />
                        </div>
                        <MentionsReachChart
                            series={chartMode === 'sentiment'
                                ? chart.sentiment_series
                                : chart.mentions_reach_series}
                            mode={chartMode}
                            bucket={bucket}
                            onSpikeClick={spike => setSelectedSpike(spike)}
                            onPointClick={range => { setBucketFilter({ from: range.from, to: range.to }); setPage(1); }} />
                        <div className="bm_chart_legend">
                            {CHART_SERIES[chartMode === 'sentiment' ? 'sentiment' : 'mentions_reach'].map(sr => (
                                <span key={sr.key} className="bm_chart_legend_item">
                                    <span className={`bm_chart_legend_dot is-${sr.key}${sr.kind === 'bar' ? ' is-bar' : ''}`} aria-hidden="true" />
                                    <Body as="span" variant={2} className="bm_chart_legend_label">{sr.label}</Body>
                                </span>
                            ))}
                        </div>
                        {selectedSpike && (
                            <SpikePanel spike={selectedSpike}
                                onClose={() => setSelectedSpike(null)}
                                onDrillIn={s => {
                                    setBucketFilter({ from: s.range.from, to: s.range.to });
                                    setPage(1);
                                }}
                                onShowAll={() => {
                                    setBucketFilter({ from: '', to: '' });
                                    setPage(1);
                                }} />
                        )}
                        {(bucketFilter.from || bucketFilter.to) && (
                            <div className="bm_bucket_filter_banner">
                                Feed filtered to{' '}
                                <strong>{bucketFilter.from === bucketFilter.to
                                    ? fmtDMY(bucketFilter.from)
                                    : `${fmtDMY(bucketFilter.from) || '…'} → ${fmtDMY(bucketFilter.to) || '…'}`}</strong>.
                                <button onClick={() => { setBucketFilter({ from: '', to: '' }); setPage(1); }}
                                    className="bm_bucket_filter_clear">
                                    Clear date filter ×
                                </button>
                            </div>
                        )}
                        {(monitoredTopic || narrativeFilter) && (
                            <div className="bm_bucket_filter_banner">
                                Feed filtered to topic{' '}
                                <strong>{monitoredTopic || narrativeFilter}</strong>.
                                <button onClick={() => {
                                    setMonitoredTopic('');
                                    setNarrativeFilter('');
                                    setPage(1);
                                }}
                                    className="bm_bucket_filter_clear">
                                    Clear topic filter ×
                                </button>
                            </div>
                        )}
                        {authorFilter && (
                            <div className="bm_bucket_filter_banner">
                                Feed filtered to author{' '}
                                <strong>{authorFilter}</strong>.
                                <button onClick={() => { setAuthorFilter(''); setPage(1); }}
                                    className="bm_bucket_filter_clear">
                                    Clear author filter ×
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="bm_feed" ref={feedRef}>
                        {(feed.results || []).map(m => (
                            <MentionCard key={m.id} m={m}
                                orgTags={orgTags}
                                onVisitChange={handleVisitChange}
                                onMute={handleMuteHost}
                                onTagsChange={handleTagsChange}
                                onOrgTagsRefresh={refreshOrgTags}
                                onQueueChange={handleQueueChange}
                                onTakedown={handleTakedownSubmitted}
                                onVerdictChange={handleVerdictChange} />
                        ))}
                        {/* 2026-05-27 UX cleanup - skeleton placeholders
                            during load instead of a blank gap. Three
                            ghost-cards match the typical first-paint
                            sized of the feed. */}
                        {feedLoading && (feed.results || []).length === 0 && (
                            <>
                                {[0, 1, 2].map(i => (
                                    <div key={i} className="bm_card cmn_elevated_card">
                                        <div style={{
                                            width: 44, height: 44, borderRadius: '50%',
                                            background: 'rgba(148,163,184,0.2)'
                                        }} />
                                        <div style={{ flex: 1 }}>
                                            <Skeleton width="35%" height={12} />
                                            <div style={{ marginTop: 6 }}>
                                                <Skeleton width="90%" height={10} />
                                            </div>
                                            <div style={{ marginTop: 4 }}>
                                                <Skeleton width="70%" height={10} />
                                            </div>
                                            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                                                <Skeleton width={80} height={20} />
                                                <Skeleton width={80} height={20} />
                                                <Skeleton width={120} height={20} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        {!feedLoading && (feed.results || []).length === 0 && (
                            <EmptyState
                                mark="line"
                                title="No mentions match these filters"
                                body={(() => {
                                    if (filtersAreCustom) {
                                        const active = [];
                                        if (platforms.length) active.push(`platforms: ${platforms.join(', ')}`);
                                        if (sentiments.length) active.push(`sentiment: ${sentiments.join(', ')}`);
                                        if (riskOnly) active.push('risk-tagged only');
                                        if (visitedMode !== 'all') active.push(`${visitedMode} only`);
                                        if (days !== 30) active.push(`last ${days}d`);
                                        return `${active.length ? `Active filters → ${active.join(' · ')}. ` : ''}Try clearing a filter, widening the date range, or hitting Refresh.`;
                                    }
                                    return 'No mentions captured in this window yet. Either the scanner hasn\'t run yet, or your watchlist anchors are too narrow. Add anchors in Settings → Mention anchors, then click Scan now.';
                                })()}
                                action={filtersAreCustom ? (
                                    <CommonButton
                                        text="Clear all filters"
                                        variant="white"
                                        className="bm_scan_btn"
                                        onClick={clearFilters}
                                    />
                                ) : (
                                    <CommonButton
                                        text="Open Settings → Mention anchors"
                                        variant="primary"
                                        className="bm_scan_btn"
                                        onClick={() => window.dispatchEvent(
                                            new CustomEvent('bm-tab-switch', { detail: { tab: 'settings' } }))}
                                    />
                                )}
                            />
                        )}
                    </div>

                    <Pagination
                        page={page} pages={feed.pages || 0}
                        total={feed.total || 0} pageSize={pageSize}
                        onPage={p => {
                            setPage(p);
                            const el = feedRef.current;
                            if (el) {
                                el.scrollTop = 0;
                                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }}
                        onPageSize={n => { setPageSize(n); setPage(1); }} />
                </div>

                <div className="bm_rail">
                    <MentionFilters
                        filterParams={filterParams}
                        visitedMode={visitedMode}
                        onVisitedChange={v => { setVisitedMode(v); setPage(1); }}
                        knownPlatforms={filterOptions.platforms}
                        platforms={platforms}
                        onTogglePlatform={v => toggleInList(v, platforms, setPlatforms)}
                        sentiments={sentiments.map(x => x.toLowerCase())}
                        onToggleSentiment={v => toggleInList(v, sentiments, setSentiments)}
                        activeLanguages={activeLanguages}
                        onToggleLanguage={toggleLanguage}
                        onOpenLanguageBreakdown={() => setLangModalOpen(true)}
                        onClearRail={() => {
                            setPlatforms([]); setSentiments([]); setActiveLanguages([]);
                            setVisitedMode('all'); setPage(1);
                        }}
                    />
                    {langModalOpen && (
                        <LanguageBreakdownModal
                            data={languageData}
                            filterParams={filterParams}
                            activeLanguages={activeLanguages}
                            onShowInFeed={l => { toggleLanguage(l); setLangModalOpen(false); }}
                            onClose={() => setLangModalOpen(false)}
                        />
                    )}
                </div>
            </div>
        </>
    );
}
